import _ from 'lodash';
import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { TranslatorContext } from '@jutro/locale';
import { WizardPage } from '@xengage/gw-portals-wizard-react';
import { BreakpointTrackerContext } from '@jutro/layout';
import UWCompanyUtil from '../../utils/UWCompanyUtil_TWIA.js';
import AgentRegistrationWizardTemplate from '../../templates/AgentRegistrationWizardTemplate.jsx';
import metadata from './AgencyInformationPage.metadata.json5';
import messages from './AgencyInformationPage.messages.js';
import styles from './AgencyInformationPage.module.scss';


function AgencyInformationPage(props) {

    const { wizardData: registrationVM, updateWizardData, id, isConfirmationPage } = props;

    const breakpoint = useContext(BreakpointTrackerContext)
    const translator = useContext(TranslatorContext);
    const [showErrors, setShowErrors] = useState(false);
    const { onValidate, isComponentValid } = useValidation(id);
    const isEOCoverageAvailable = _.get(registrationVM, 'eoinsuranceInfo.hasEOCoverage.value');
    const isMailingAddressAvailable = _.get(registrationVM, 'hasMailingAddress.value');
    const isTWIA = UWCompanyUtil.isTWIACompany(_.get(registrationVM, 'uwcompany'))
    const [eOExpirationDateValidationMessage, setEOExpirationDateValidationMessage] = useState([]);
    const [emailValidationMessage, setEmailValidationMessage] = useState([]);
    const [confirmEmailValidationMessage, setConfirmEmailValidationMessage] = useState([]);
    const [phoneValidationMessage, setPhoneValidationMessage] = useState([]);
    const emailPattern = /^(?:(?!\b(?:example|test|localhost|invalid|local|localdomain|domain|lan|home|host|corp)\b)).+@(?:(?!\b(?:example|test|localhost|invalid|local|localdomain|domain|lan|home|host|corp)\b))[A-Za-z0-9.-]+\.(?!(?:example|test|localhost|invalid|local|localdomain|domain|lan|home|host|corp)\b)[A-Za-z]{2,25}$/i;
    const isEmail = (email) => emailPattern.test(email);
    const isTFPADomain = (email) => /^[a-z0-9]+@txfb-ins.com$/i.test(email);
    const isTWIADomain = (email) => /^[a-z0-9]+@twfg.com$/i.test(email);
    const isPhoneNumberValid = (phone) => /^[2-9]\(?([0-9]{2})\)?[-.●]?([0-9]{3})[-.●]?([0-9]{4})$/.test(phone);
    const accountEmail = _.get(registrationVM, 'email.value');
    const confirmEmail = _.get(registrationVM, 'verifyEmail_TWIA.value');
    const agencyAddressId = _.get(registrationVM, 'mailingAddress.displayName.value');
    const agencyVerification = _.get(registrationVM, 'isAgencyVerification.value')

    useEffect(() => {
        if (!isConfirmationPage) {
            const myInput = document.getElementsByName('confirmEmailAddress');

            myInput[0].onpaste = () => false;
        }
    }, [isConfirmationPage]);

    const handleEOCoverage = useCallback(
        (value, path) => {
            
            if (value === false) {
                _.set(registrationVM, 'eoinsuranceInfo.eoissuingCarrier.value', undefined);
                _.set(registrationVM, 'eOInsuranceInfo.eolimits.value', undefined);
                _.set(registrationVM, 'eoinsuranceInfo.eoexpirationDate.value', undefined);
            }

            _.set(registrationVM, path, value);
            updateWizardData(registrationVM);
        },
        [registrationVM, updateWizardData]
    );

    const handleMailingAddress = useCallback(
        (value, path) => {

            if (value === false) {
                registrationVM.agencyAddresses.aspects.typedValue.some(element => {
                    if (element.displayName === agencyAddressId) {
                        _.set(registrationVM, 'mailingAddress.addressLine1', element.addressLine1);
                        _.set(registrationVM, 'mailingAddress.addressLine2', element.addressLine2);
                        _.set(registrationVM, 'mailingAddress.addressLine3', element.addressLine3);
                        _.set(registrationVM, 'mailingAddress.city', element.city);
                        _.set(registrationVM, 'mailingAddress.postalCode', element.postalCode);
                        _.set(registrationVM, 'mailingAddress.state', element.state);
                    }
                });
            } else {
                _.set(registrationVM, 'mailingAddress.addressLine1', undefined);
                _.set(registrationVM, 'mailingAddress.addressLine2', undefined);
                _.set(registrationVM, 'mailingAddress.addressLine3', undefined);
                _.set(registrationVM, 'mailingAddress.city', undefined);
                _.set(registrationVM, 'mailingAddress.postalCode', undefined);
                _.set(registrationVM, 'mailingAddress.state', undefined);
            }

            _.set(registrationVM, path, value);
            updateWizardData(registrationVM);
        },
        [registrationVM, updateWizardData, agencyAddressId]
    );

    const getUSStateList = useMemo(() => {
        const stateTypelist = registrationVM.mailingAddress.state.aspects.availableValues.map((code) => {
            return {
                code: code.code,
                name: translator({ id: code.name })
            }
        })

        return stateTypelist
    }, [registrationVM, translator]);

    const setAvailableAgencyAddressValue = useMemo(() => {
        const agencyAddress =
            registrationVM.agencyAddresses.aspects.typedValue.map(key => {
                return {
                    name: translator({
                        id: key.recordID,
                        defaultMessage: key.displayName,
                    }),
                    code: key.recordID,
                };
            })

        return agencyAddress;
    }, [registrationVM, translator]);

    const eOExpirationDateChange = useCallback(
        (value, path) => {
            _.set(registrationVM, path, value);
            setEOExpirationDateValidationMessage([]);

            if (new Date(value).getTime() < new Date().getTime()) {

                setEOExpirationDateValidationMessage([translator(messages.EOExpirationDateValidationMessage)])
            }
            
            // DE17365
            if(value === undefined){
                setEOExpirationDateValidationMessage([translator(messages.Required)]);
            }

            updateWizardData(registrationVM);
        },
        [registrationVM, updateWizardData, translator]);

    const handleValueChange = useCallback(
        (value, path) => {

            registrationVM.agencyAddresses.aspects.typedValue.some(element => {
                if (element.recordID === value) {
                    let telephone = element.telephone;

                    if(element.telephone?.length === 10) {

                        telephone = element.telephone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3")
                      }

                    _.set(registrationVM, 'agencyContact.telephone', telephone);
                    _.set(registrationVM, 'agentEFTDTO_TWIA.telephone', element.telephone);
                    _.set(registrationVM, 'agentEFTDTO_TWIA.mailingAddress.addressLine1', element.addressLine1);
                    _.set(registrationVM, 'agentEFTDTO_TWIA.mailingAddress.addressLine2', element.addressLine2);
                    _.set(registrationVM, 'agentEFTDTO_TWIA.mailingAddress.addressLine3', element.addressLine3);
                    _.set(registrationVM, 'agentEFTDTO_TWIA.mailingAddress.city', element.city);
                    _.set(registrationVM, 'agentEFTDTO_TWIA.mailingAddress.postalCode', element.postalCode);
                    _.set(registrationVM, 'agentEFTDTO_TWIA.mailingAddress.state', element.state);
                    _.set(registrationVM, 'taxAddress.addressLine1', element.addressLine1);
                    _.set(registrationVM, 'taxAddress.addressLine2', element.addressLine2);
                    _.set(registrationVM, 'taxAddress.addressLine3', element.addressLine3);
                    _.set(registrationVM, 'taxAddress.city', element.city);
                    _.set(registrationVM, 'taxAddress.postalCode', element.postalCode);
                    _.set(registrationVM, 'taxAddress.state', element.state);


                    if (!isMailingAddressAvailable) {
                        _.set(registrationVM, 'mailingAddress.displayName', element.displayName);
                        _.set(registrationVM, 'mailingAddress.addressLine1', element.addressLine1);
                        _.set(registrationVM, 'mailingAddress.addressLine2', element.addressLine2);
                        _.set(registrationVM, 'mailingAddress.addressLine3', element.addressLine3);
                        _.set(registrationVM, 'mailingAddress.city', element.city);
                        _.set(registrationVM, 'mailingAddress.postalCode', element.postalCode);
                        _.set(registrationVM, 'mailingAddress.state', element.state);
                    }
                }
            });
            _.set(registrationVM, path, value);
            updateWizardData(registrationVM);
        },
        [registrationVM, updateWizardData, isMailingAddressAvailable]);

    const validateEmail = useCallback(
        (value, path) => {
            _.set(registrationVM, path, value);
            setEmailValidationMessage([]);

            if (!value) {

                setEmailValidationMessage([translator(messages.Required)])
            } else if (isTWIA && (isTFPADomain(value) || isTWIADomain(value))) {

                setEmailValidationMessage([translator(messages.TWIAEmailValidationError)])
            } else if (!isTWIA && (isTFPADomain(value) || isTWIADomain(value))) {

                setEmailValidationMessage([translator(messages.TFPAEmailValidationError)])
            } else if (!isEmail(value)) {

                setEmailValidationMessage([translator(messages.EmailValidationMessage)])
            }

            updateWizardData(registrationVM);
        },
        [registrationVM, updateWizardData, isTWIA, translator]);

    const validateConfirmEmail = useCallback(
        (value, path) => {
            _.set(registrationVM, path, value);
            setConfirmEmailValidationMessage([]);

            if (!value) {

                setConfirmEmailValidationMessage([translator(messages.Required)])
            } else if (!isEmail(value)) {

                setConfirmEmailValidationMessage([translator(messages.EmailValidationMessage)])
            } else if (accountEmail !== value) {

                setConfirmEmailValidationMessage([translator(messages.EmailAddressMatchError)])
            }

            updateWizardData(registrationVM);
        },
        [registrationVM, updateWizardData, translator, accountEmail]);

    const validatePhoneNumber = useCallback(
        (value, path) => {

            if (value.match(/[a-z A-Z]/g)) {
                return false;
            }

            if (value.length === 3 || value.length === 7) {
                value += "-"
            }

            setPhoneValidationMessage([]);

            if (!value) {

                setPhoneValidationMessage([translator(messages.Required)])
            } else if (!isPhoneNumberValid(value) || value.match(/[0-9]/g).length < 10) {

                setPhoneValidationMessage([translator(messages.PhoneValidationMessage)])
            }

            _.set(registrationVM, path, value);
            updateWizardData(registrationVM);
            // DE17849
            setPhoneValidationMessage(registrationVM?.agencyContact?.telephone?.aspects?.validationMessages)
        },
        [registrationVM, updateWizardData, translator]);

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showErrors,
            readOnly: isConfirmationPage
        },
        tfpaRegistrationInstruction: {
            visible: !isTWIA  && !isConfirmationPage
        },
        twiaRegistrationInstruction: {
            visible: isTWIA  && !isConfirmationPage
        },
        primaryLocationAddress: {
            availableValues: setAvailableAgencyAddressValue,
            value: _.get(registrationVM, 'tdiaddressLookup.value'),
            label: isTWIA ? translator(messages.PrimaryLocationAddress) : translator(messages.AgencyAddress)
        },
        isMailingAddressDifferent: {
            value: isMailingAddressAvailable,
            className: isConfirmationPage ? '' : styles.toggleReverse
        },
        addressLine1: {
            visible: isMailingAddressAvailable,
            showRequired: true
        },
        addressLine2: {
            visible: isMailingAddressAvailable,
        },
        addressLine3: {
            visible: isMailingAddressAvailable,
        },
        city: {
            visible: isMailingAddressAvailable,
            showRequired: true
        },
        zipCode: {
            visible: isMailingAddressAvailable,
            showRequired: true
        },
        state: {
            visible: isMailingAddressAvailable,
            showRequired: true,
            availableValues: !isConfirmationPage && getUSStateList,
            value: _.get(registrationVM.value, 'mailingAddress.state')
        },
        confirmEmailAddress: {
            showRequired: true,
            required: true,
            validationMessages: confirmEmailValidationMessage
        },
        EOCoverageTitle: {
            visible: !isTWIA
        },
        EOCoverage: {
            visible: !isTWIA,
            value: _.get(registrationVM, 'eoinsuranceInfo.hasEOCoverage.value'),
        },
        EOIssuingCarrier: {
            visible: isEOCoverageAvailable,
            showRequired: true
        },
        EOLimits: {
            visible: isEOCoverageAvailable,
            showRequired: true
        },
        EOExpirationDate: {
            visible: isEOCoverageAvailable,
            showRequired: true,
            validationMessages: _.isUndefined(_.get(registrationVM,'eoinsuranceInfo.eoexpirationDate.value')) ? 
            [translator(messages.Required)] : eOExpirationDateValidationMessage // DE17365
        },
        AgencyInfoContainer: {
            visible: !isConfirmationPage
        },
        licenseNumber: {
            visible: isConfirmationPage ? true : false
            
        },
        emailAddress: {
            validationMessages:emailValidationMessage
        },
        phoneNumber: {
            // DE17789
            validationMessages: _.isEmpty(phoneValidationMessage) && 
            (_.get(registrationVM, 'agencyContact.telephone.value') === undefined || 
            _.get(registrationVM, 'agencyContact.telephone.value') === "") &&
            _.get(registrationVM, 'agencyContact.telephone.aspects.required')
            ? [translator(messages.Required)] : phoneValidationMessage
        },
        confirmMsg: {
            visible: !!accountEmail && !!confirmEmail && accountEmail === confirmEmail && emailValidationMessage?.length === 0
        }

    };

    const onNext = useCallback(async () => {
        if (!isComponentValid) {
            setShowErrors(true)
            return false
        }
        return registrationVM
    }, [isComponentValid, registrationVM])

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onHandleEOCoverage: handleEOCoverage,
            onHandleMailingAddress: handleMailingAddress,
            OnEOExpirationDateChange: eOExpirationDateChange,
            handleValueChange: handleValueChange,
            OnValidateEmail: validateEmail,
            OnValidatePhoneNumber: validatePhoneNumber,
            OnValidateConfirmEmail: validateConfirmEmail


        },
    };

    return (
        isConfirmationPage ? 
        <ViewModelForm
                uiProps={metadata.pageContent}
                model={registrationVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
            /> :
        <WizardPage
            id={id}
            onNext={onNext}
            showPrevious={!agencyVerification} 
            template={AgentRegistrationWizardTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={registrationVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

export default AgencyInformationPage;